'use client'

import * as React from 'react'
import Image from 'next/image'
import { ChevronLeftIcon, ChevronRightIcon } from 'primitives/icons'

interface ComparisonSliderProps {
  beforeSrc: string
  afterSrc: string
  beforeLabel: string
  afterLabel: string
  className?: string
}

/**
 * Minimal spring animation using requestAnimationFrame.
 * Replaces framer-motion's `animate()` to avoid pulling in the library.
 */
function animateSpring(
  from: number,
  to: number,
  onUpdate: (value: number) => void,
  onComplete?: () => void,
) {
  let velocity = 0
  let value = from
  const stiffness = 400
  const damping = 32
  const precision = 0.01
  const dt = 1 / 60
  let rafId: number

  function step() {
    const force = -stiffness * (value - to)
    const dampingForce = -damping * velocity
    velocity += (force + dampingForce) * dt
    value += velocity * dt

    onUpdate(value)

    if (Math.abs(value - to) < precision && Math.abs(velocity) < precision) {
      onUpdate(to)
      onComplete?.()
      return
    }

    rafId = requestAnimationFrame(step)
  }

  rafId = requestAnimationFrame(step)

  return { stop: () => cancelAnimationFrame(rafId) }
}

function useSliderDrag(snapPoints: number[] = []) {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const isDragging = React.useRef(false)
  const cachedRect = React.useRef<DOMRect | null>(null)
  const currentValue = React.useRef(50)
  const snapAnimation = React.useRef<{ stop: () => void } | null>(null)
  const rafId = React.useRef<number | null>(null)
  const pendingClientX = React.useRef<number | null>(null)

  // Refs for direct DOM updates during drag
  const beforeClipRef = React.useRef<HTMLDivElement>(null)
  const afterClipRef = React.useRef<HTMLDivElement>(null)
  const beforeLabelClipRef = React.useRef<HTMLDivElement>(null)
  const afterLabelClipRef = React.useRef<HTMLDivElement>(null)
  const beforeLabelRef = React.useRef<HTMLDivElement>(null)
  const afterLabelRef = React.useRef<HTMLDivElement>(null)
  const handleRef = React.useRef<HTMLDivElement>(null)

  // State only for initial render — drag updates bypass React
  const [initialValue] = React.useState(50)

  React.useEffect(
    () => () => {
      snapAnimation.current?.stop()
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current)
      }
    },
    [],
  )

  const applyValue = React.useCallback((value: number) => {
    currentValue.current = value
    const before = beforeClipRef.current
    const after = afterClipRef.current
    const beforeLabelClip = beforeLabelClipRef.current
    const afterLabelClip = afterLabelClipRef.current
    const beforeLabel = beforeLabelRef.current
    const afterLabel = afterLabelRef.current
    const handle = handleRef.current

    if (before) {
      before.style.clipPath = `inset(0 ${100 - value}% 0 0)`
    }
    if (after) {
      after.style.clipPath = `inset(0 0 0 ${value}%)`
    }
    if (beforeLabelClip) {
      beforeLabelClip.style.clipPath = `inset(0 ${100 - value}% 0 0)`
    }
    if (afterLabelClip) {
      afterLabelClip.style.clipPath = `inset(0 0 0 ${value}%)`
    }
    if (beforeLabel) {
      beforeLabel.style.left = `${value / 2}%`
    }
    if (afterLabel) {
      afterLabel.style.left = `${value + (100 - value) / 2}%`
    }
    if (handle) {
      handle.style.left = `${value}%`
    }
  }, [])

  const processPointerMove = React.useCallback(() => {
    rafId.current = null
    const clientX = pendingClientX.current
    if (clientX === null || !cachedRect.current) {
      return
    }
    const rect = cachedRect.current
    const percent = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100))
    applyValue(percent)
  }, [applyValue])

  const handlePointerDown = React.useCallback(
    (event: React.PointerEvent) => {
      event.preventDefault()
      isDragging.current = true
      snapAnimation.current?.stop()
      // Cache the rect once — it won't change during a drag
      cachedRect.current = containerRef.current?.getBoundingClientRect() ?? null
      containerRef.current?.setPointerCapture(event.pointerId)
      const rect = cachedRect.current
      if (rect) {
        const percent = Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100))
        applyValue(percent)
      }
    },
    [applyValue],
  )

  const handlePointerMove = React.useCallback(
    (event: React.PointerEvent) => {
      if (!isDragging.current) {
        return
      }
      // Coalesce rapid events into a single rAF
      pendingClientX.current = event.clientX
      if (rafId.current === null) {
        rafId.current = requestAnimationFrame(processPointerMove)
      }
    },
    [processPointerMove],
  )

  const handlePointerUp = React.useCallback(() => {
    if (!isDragging.current) {
      return
    }
    isDragging.current = false
    cachedRect.current = null
    if (!snapPoints.length) {
      return
    }

    const current = currentValue.current
    const nearest = snapPoints.reduce((closest, point) =>
      Math.abs(point - current) < Math.abs(closest - current) ? point : closest,
    )
    snapAnimation.current?.stop()
    snapAnimation.current = animateSpring(current, nearest, applyValue)
  }, [snapPoints, applyValue])

  return {
    containerRef,
    initialValue,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    beforeClipRef,
    afterClipRef,
    beforeLabelClipRef,
    afterLabelClipRef,
    beforeLabelRef,
    afterLabelRef,
    handleRef,
  }
}

export function ComparisonSlider({
  beforeSrc,
  afterSrc,
  beforeLabel,
  afterLabel,
  className = '',
}: ComparisonSliderProps) {
  const {
    containerRef,
    initialValue,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    beforeClipRef,
    afterClipRef,
    beforeLabelClipRef,
    afterLabelClipRef,
    beforeLabelRef,
    afterLabelRef,
    handleRef,
  } = useSliderDrag([0, 50, 100])

  return (
    <div
      ref={containerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      className={`relative aspect-[748/320] touch-none overflow-hidden rounded-lg bg-[#111] ${className}`}
    >
      {/* Before (static image) */}
      <div
        ref={beforeClipRef}
        className="absolute inset-0 select-none overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - initialValue}% 0 0)` }}
      >
        <Image
          alt=""
          src={beforeSrc}
          fill
          sizes="(min-width: 1536px) 748px, (min-width: 1024px) 600px, 100vw"
          className="pointer-events-none object-cover"
          draggable={false}
        />
      </div>

      {/* After (video) */}
      <div
        ref={afterClipRef}
        className="absolute inset-0 select-none overflow-hidden bg-[#111]"
        style={{ clipPath: `inset(0 0 0 ${initialValue}%)` }}
      >
        <video
          autoPlay
          loop
          muted
          playsInline
          src={afterSrc}
          className="pointer-events-none size-full object-cover"
          draggable={false}
          onError={e => e.stopPropagation()}
        />
      </div>

      {/* Before label */}
      <div
        ref={beforeLabelClipRef}
        className="pointer-events-none absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - initialValue}% 0 0)` }}
      >
        <div
          ref={beforeLabelRef}
          className="absolute bottom-3 -translate-x-1/2 whitespace-nowrap"
          style={{ left: `${initialValue / 2}%` }}
        >
          <span className="rounded-full bg-cf-black/60 px-3 py-1 text-xs font-medium text-cf-white backdrop-blur-sm">
            {beforeLabel}
          </span>
        </div>
      </div>

      {/* After label */}
      <div
        ref={afterLabelClipRef}
        className="pointer-events-none absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 0 0 ${initialValue}%)` }}
      >
        <div
          ref={afterLabelRef}
          className="absolute bottom-3 -translate-x-1/2 whitespace-nowrap"
          style={{ left: `${initialValue + (100 - initialValue) / 2}%` }}
        >
          <span className="rounded-full bg-cf-black/60 px-3 py-1 text-xs font-medium text-cf-white backdrop-blur-sm">
            {afterLabel}
          </span>
        </div>
      </div>

      {/* Drag handle */}
      <div
        ref={handleRef}
        className="pointer-events-none absolute inset-y-0 w-1 -translate-x-1/2 bg-cf-white/90 shadow-sm"
        style={{ left: `${initialValue}%` }}
      >
        <div className="absolute left-1/2 top-1/2 flex size-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-gray-300 bg-cf-white shadow-md">
          <ChevronLeftIcon className="size-4 -mr-1" />
          <ChevronRightIcon className="size-4 -ml-1" />
        </div>
      </div>
    </div>
  )
}
